<script setup lang="ts">
import { CalendarDays, CheckCircle2, ExternalLink, ListChecks, Search, ShieldCheck } from "lucide-vue-next";
import type { BriefSolution } from "@/types";

defineProps<{ solution: BriefSolution | null; confirming?: boolean }>();
const emit = defineEmits<{ confirm: [] }>();
</script>

<template>
  <section class="solution-panel">
    <header>
      <div>
        <span class="eyebrow">完整方案</span>
        <h2>{{ solution?.clientContent.title || "等待 AI 整理方案" }}</h2>
      </div>
      <span v-if="solution" class="readiness">完整度 {{ solution.readiness.score }}%</span>
    </header>
    <template v-if="solution">
      <div class="solution-copy">{{ solution.clientContent.fullPlan || solution.clientContent.summary }}</div>
      <section v-if="solution.clientContent.researchSources?.length" class="research">
        <h3><Search :size="17" />联网参考</h3>
        <p v-if="solution.clientContent.researchSummary">{{ solution.clientContent.researchSummary }}</p>
        <a v-for="source in solution.clientContent.researchSources" :key="source.url" :href="source.url" target="_blank" rel="noopener noreferrer"><span><b>{{ source.title }}</b><small>{{ source.url }}</small></span><ExternalLink :size="14" /></a>
      </section>
      <section class="solution-tasks">
        <h3><ListChecks :size="17" />两级执行清单</h3>
        <article v-for="(task, index) in solution.tasks || []" :key="task.stableKey">
          <span>{{ index + 1 }}</span>
          <div>
            <b>{{ task.titleClient }}</b>
            <p>{{ task.scopeClient }}</p>
            <div class="task-meta"><CalendarDays :size="14" />{{ task.plannedStartAt }} 至 {{ task.plannedEndAt }}</div>
            <ol class="groups">
              <li v-for="group in task.checklist" :key="group.stableKey">
                <div class="group-head"><b>{{ group.title }}</b><time>{{ group.plannedStartAt }} 至 {{ group.plannedEndAt }}</time></div>
                <p>{{ group.description }}</p>
                <ul>
                  <li v-for="child in group.children" :key="child.stableKey">
                    <div><b>{{ child.title }}</b><time>{{ child.plannedStartAt }} 至 {{ child.plannedEndAt }}</time></div>
                    <p>{{ child.description }}</p>
                    <small>核查：{{ child.acceptanceCriteria.join("；") }}</small>
                  </li>
                </ul>
              </li>
            </ol>
          </div>
        </article>
      </section>
      <section v-if="solution.requirements?.length" class="requirements">
        <h3><ShieldCheck :size="17" />提交前核查事项</h3>
        <article v-for="item in solution.requirements" :key="item.id">
          <b>{{ item.title }}</b><p>{{ item.description }}</p>
        </article>
      </section>
      <div v-if="solution.readiness.warnings.length" class="warnings">
        <b>仍可补充</b>
        <ul><li v-for="item in solution.readiness.warnings" :key="item">{{ item }}</li></ul>
      </div>
      <button
        class="btn btn--primary confirm"
        :disabled="!solution.readiness.readyForClientReview || confirming"
        @click="emit('confirm')"
      >
        <CheckCircle2 :size="17" />
        {{ confirming ? "正在创建项目草稿" : "确认方案并创建项目草稿" }}
      </button>
      <p v-if="!solution.readiness.readyForClientReview" class="muted gate">
        方案仍缺少：{{ solution.readiness.missingBlockingFields.join("、") }}
      </p>
    </template>
    <p v-else class="muted empty">发送需求后，AI 的完整方案会显示在这里。</p>
  </section>
</template>

<style scoped>
.solution-panel{display:grid;gap:16px}.solution-panel>header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.solution-panel h2,.solution-panel h3{margin:0}.solution-panel h2{font-size:18px}.eyebrow{display:block;margin-bottom:3px;color:var(--muted);font-size:12px}.readiness{white-space:nowrap;padding:4px 7px;background:#e8f5f1;color:var(--success);font-size:12px}.solution-copy{white-space:pre-wrap;padding:14px 0;border-top:1px solid var(--line);border-bottom:1px solid var(--line);line-height:1.7}.research{display:grid;gap:6px;padding:12px 0;border-bottom:1px solid var(--line)}.research h3{display:flex;align-items:center;gap:7px;font-size:15px}.research>p{margin:0;color:var(--muted)}.research a{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:8px;border:1px solid var(--line);color:var(--ink);text-decoration:none}.research a span{min-width:0}.research a b,.research a small{display:block}.research a small{overflow:hidden;color:var(--muted);font-size:10px;text-overflow:ellipsis;white-space:nowrap}.solution-tasks{display:grid;gap:8px}.solution-tasks h3,.requirements h3{display:flex;align-items:center;gap:7px;font-size:15px}.solution-tasks>article{display:grid;grid-template-columns:26px minmax(0,1fr);gap:8px;padding:12px 0;border-bottom:1px solid var(--line)}.solution-tasks article>span{display:grid;width:24px;height:24px;place-items:center;background:#f1f4f4;color:var(--muted);font-size:12px}.solution-tasks p{margin:3px 0;color:var(--muted)}.task-meta{display:flex;align-items:center;gap:5px;margin-top:7px;color:var(--muted);font-size:12px}.groups{display:grid;gap:9px;margin:12px 0 0;padding:0;list-style:none}.groups>li{padding:10px;border-left:3px solid #81949a;background:#f8fafa}.group-head,.groups ul li>div{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}.groups time{white-space:nowrap;color:var(--muted);font-size:11px}.groups ul{display:grid;gap:7px;margin:9px 0 0;padding:0 0 0 18px;list-style:none}.groups ul li{padding:8px 0;border-top:1px solid var(--line)}.groups small{display:block;margin-top:4px;color:var(--brand-dark)}.requirements{display:grid;gap:0;padding:13px 0;border-top:1px solid var(--line)}.requirements article{padding:9px 0;border-bottom:1px solid var(--line)}.requirements p{margin:3px 0;color:var(--muted)}.warnings{padding:11px;border-left:3px solid #d98b21;background:#fff8ed}.warnings ul{margin:5px 0 0;padding-left:18px}.confirm{display:flex;align-items:center;justify-content:center;gap:7px;width:100%}.gate,.empty{margin:0;text-align:center}@media(max-width:620px){.group-head,.groups ul li>div{display:grid}.groups time{white-space:normal}}
</style>
